import _ from "lodash";
import React, { useEffect, useState } from "react";

// Components
import FieldSelectionModal from "./FieldSelectionModal";

// DS
import { Button, Tooltip } from "@toddle-edu/ds-web";
import { TickCircleFilled } from "@toddle-edu/ds-icons";

// Utils
import { getFieldValue } from "Assessments/modules/Utils";
import {
  getCurrentFieldConfig,
  getFieldHasValue,
  getIsMYPInterdiscplinaryUnit,
  getEntityAndFieldType,
  getGroupedFields,
  getAssessmentToolFromField,
} from "./Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";

// Constants
import { ASSESSMENT } from "Constants/stringConstants";

// third party
import { connect } from "react-redux";
import classNames from "classnames";

// modules
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { updateAIMessage } from "modules/Services";
import { getAssessmentObj } from "ClassRoom/modules/ClassRoomModule";

// Scss
import classes from "./PrerequisiteSelection.scss";

const PrerequisiteSelection = props => {
  const {
    t,
    context,
    messages,
    fieldList,
    curriculumType,
    assessmentType,
    unitPlanDetails,
    unitPlanTemplate,
    setSelectedAction,
    assessmentDetails,
    prerequisiteFieldsData,
    isEveryPrerequisiteFieldFilled,
    excludedFields,
    setExcludedFields,
    fieldsToRemove,
    groupedFields,
    updateAIMessage,
    stepList,
    currentAIState,
    getAssessmentObj,
    setContext,
    measureAssessingRef,
  } = props;

  const [selectedField, setSelectedField] = useState({});

  // when all the prerequisites are filled and then again generate AI response with fieldType and action
  useEffect(() => {
    const skipGPTCall = _.get(prerequisiteFieldsData, "skipGPTCall", false);

    const generateAIResponse =
      !skipGPTCall &&
      isEveryPrerequisiteFieldFilled &&
      _.isEmpty(selectedField?.fields) &&
      !_.isEqual(currentAIState, "STOP");

    if (generateAIResponse) {
      const lastUserMessage = _.findLast(
        messages,
        ({ node } = {}) => node?.authorType === "user"
      );

      const displayText = _.get(lastUserMessage, "node.content", "");

      // 500 ms delay to show fadeout animation (which is also of 500ms)
      setTimeout(() => {
        setSelectedAction({
          ..._.pick(prerequisiteFieldsData, ["fieldType", "action"]),
          displayText,
          createUserMessage: false,
        });
      }, 500);
    }
  }, [isEveryPrerequisiteFieldFilled, selectedField]);

  // if user skipped some fields and then later on added data for them then remove those fields from excludedFields
  useEffect(() => {
    if (!_.isEmpty(fieldsToRemove)) {
      const updatedExcludedFields = _.filter(
        excludedFields,
        excludedField => !_.includes(fieldsToRemove, excludedField)
      );

      setExcludedFields(updatedExcludedFields);
    }
  }, [fieldsToRemove, excludedFields]);

  // groupedFields can change based on unit selection, so initially if user had not selected the unit then later selected it
  // we need to check if new and old pre-requisite are not the same, if they aren't they update the AI message with latest pre-requisite
  useEffect(() => {
    if (!_.isEqual(groupedFields, prerequisiteFieldsData?.fields)) {
      const lastPrerequisiteMsg = _.findLast(
        messages,
        ({ node } = {}) => node?.authorType === "prerequisite"
      );

      const id = _.get(lastPrerequisiteMsg, "node.id");

      if (id) {
        updateAIMessage({
          variables: {
            input: {
              id,
              metadata: {
                ...prerequisiteFieldsData,
                fields: groupedFields,
              },
            },
          },
        });
      }
    }
  }, [groupedFields, prerequisiteFieldsData]);

  // variables
  const typeObj = getAssessmentObj({
    curriculumType,
    assessmentValue: assessmentType,
    contentType: ASSESSMENT,
  });

  const { typeLabel, typeLabel2 } = typeObj || {};
  const assessmentTypeLabel = typeLabel || typeLabel2;

  const { field_list: unitPlanFieldList, step_list: unitPlanStepList } =
    unitPlanTemplate || {};

  // functions
  const onBlockClick = params => {
    const { field, step, disabled, isUnitDependent, config } = params;
    const { unitPlanFields } = unitPlanDetails || {};

    let fields = [{ field, config }];

    switch (field) {
      case "content": {
        if (getIsMYPInterdiscplinaryUnit(unitPlanFields)) {
          fields = [{ field: "disciplinaryContent", config }];
        }
        break;
      }
      case "title": {
        if (isUnitDependent) {
          const unitPlanValue = getFieldValue({
            fields: assessmentDetails?.allFields,
            uid: "unitPlan",
          });

          const unitPlanTitle = getFieldValue({
            fields: unitPlanFields,
            uid: "title",
          });

          if (_.isEmpty(unitPlanValue)) {
            fields = [{ field: "unitPlan", config }];
          } else if (_.isEmpty(unitPlanTitle)) {
            fields = [{ field: "title", config }];
          }
        }
        break;
      }

      default: {
        const assessmentTool = getAssessmentToolFromField({ field });

        if (!_.isEmpty(assessmentTool)) {
          const assessmentToolType = _.get(assessmentTool, "type");

          // create a new tool
          const params = {
            key: assessmentToolType,
            keyPath: [assessmentToolType],
          };

          measureAssessingRef?.onChangeDropDownMenuOption?.(params);

          return;
        }
      }
    }

    if (!disabled) {
      const _stepList = isUnitDependent ? unitPlanStepList : stepList;

      // if step is defined for any pre-requisite then render all the fields of step
      if (_stepList?.[step]) {
        const stepFields = _.get(_stepList, `${step}.fields`, []);
        setSelectedField({
          fields: _.map(stepFields, field => ({ field })),
          step,
          isUnitDependent,
        });
      } else {
        setSelectedField({ fields, step, isUnitDependent });
      }
    }
  };

  // if user has skipped any field then show it as skipped else show it as skip
  const onClickSkip = field => {
    // if user skip after adding data then remove that data from context
    const fieldUID = _.last(_.split(field, "_"));
    if (!_.isEmpty(_.get(context, fieldUID))) {
      setContext(_.omit(context, fieldUID));
    }

    if (_.includes(excludedFields, field)) {
      const updatedExcludedFields = _.filter(
        excludedFields,
        excludedField => excludedField !== field
      );

      setExcludedFields(updatedExcludedFields);
    } else {
      setExcludedFields(prevState => [...prevState, field]);
    }
  };

  // classes
  const containerClass = classNames({
    [classes.container]: true,
    [classes.containerFadeOut]:
      isEveryPrerequisiteFieldFilled && _.isEmpty(selectedField?.fields),
  });

  return (
    <div className={containerClass}>
      <div className={classes.headerText}>
        {t("classRoom:prerequisite_required_label")}
      </div>

      {_.map(prerequisiteFieldsData?.fields, (fields, key) => {
        const isUnitDependent = key === "UNIT";

        const entityLabel = isUnitDependent
          ? t("common:unit")
          : t("common:capitalize", {
              text: t(assessmentTypeLabel),
            });

        return (
          <div key={key} className={classes.blockContainer}>
            <div className={classes.blockHeader}>
              {t("classRoom:assessment_details", {
                label: entityLabel,
              })}
            </div>

            {_.map(fields, currentField => {
              const { field: key, type, step, config } = currentField || {};

              const [entity, field] = getEntityAndFieldType({
                key,
                type,
                unitPlanDetails,
                assessmentDetails,
              });

              const isUnitDependent = entity === "UNIT";

              const fieldData = getCurrentFieldConfig({
                t,
                field,
                step,
                type,
                context,
                assessmentDetails,
                unitPlanDetails,
                entityLabel,
                isUnitDependent,
                fieldList,
                unitPlanFieldList,
                stepList,
                unitPlanStepList,
                fieldConfig: config,
              });

              const {
                disabled,
                disabledTooltip,
                hasValue,
                label,
                buttonLabel,
                showSkipButton,
                showField = true,
              } = fieldData || {};

              const blockClass = classNames({
                [classes.block]: true,
                [classes.disabledBlock]: disabled,
              });

              if (!showField) return null;

              const _onBlockClick = () => {
                onBlockClick({
                  key,
                  field,
                  disabled,
                  isUnitDependent,
                  step,
                  config,
                });
              };

              return (
                <div key={field} className={blockClass}>
                  <Tooltip
                    tooltip={label}
                    placement={"top"}
                    visible={_.size(label) > 45} // 45 is max character set in multiLineEllipsis for blockLeftContainer
                    showArrow={true}
                  >
                    <div className={classes.blockLeftContainer}>{label}</div>
                  </Tooltip>
                  <div className={classes.blockRightContainer}>
                    {showSkipButton ? (
                      _.includes(excludedFields, type) ? (
                        <Button
                          icon={<TickCircleFilled variant={"success"} />}
                          variant={"progressive"}
                          type="inline"
                          size={"medium"}
                          onClick={() => onClickSkip(type)}
                          shouldStopPropagation={true}
                        >
                          {t("common:skipped")}
                        </Button>
                      ) : (
                        <Button
                          variant={"neutral"}
                          type="inline"
                          size={"medium"}
                          onClick={() => onClickSkip(type)}
                          shouldStopPropagation={true}
                        >
                          {t("common:skip")}
                        </Button>
                      )
                    ) : null}

                    {hasValue ? (
                      <Button
                        icon={<TickCircleFilled variant={"success"} />}
                        size={"medium"}
                        variant={"neutral"}
                        type="plain"
                        onClick={_onBlockClick}
                      >
                        <Tooltip tooltip={buttonLabel} showArrow={true}>
                          <div className={classes.selectedButton}>
                            {buttonLabel}
                          </div>
                        </Tooltip>
                      </Button>
                    ) : (
                      <ConditionalWrapper
                        type={"wrapperAsElement"}
                        condition={disabled && disabledTooltip}
                        wrapper={
                          <Tooltip
                            tooltip={disabledTooltip}
                            placement={"bottom"}
                            showArrow={true}
                          />
                        }
                      >
                        <Button
                          size={"medium"}
                          variant={"neutral"}
                          type="inline"
                          disabled={disabled}
                          onClick={_onBlockClick}
                        >
                          <Tooltip tooltip={buttonLabel} showArrow={true}>
                            <span>{buttonLabel}</span>
                          </Tooltip>
                        </Button>
                      </ConditionalWrapper>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {!_.isEmpty(selectedField?.fields) ? (
        <FieldSelectionModal
          {...props}
          {...(selectedField || {})}
          onClose={() => setSelectedField({})}
          assessmentTypeLabel={assessmentTypeLabel}
        />
      ) : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    t,
    context,
    assessmentDetails,
    prerequisiteFieldsData,
    unitPlanDetails,
    fieldList,
    unitPlanTemplate,
    excludedFields,
    middlePaneRef,
    stepList,
  } = ownProps;

  const assessmentType = getFieldValue({
    fields: assessmentDetails?.allFields,
    uid: "assessmentType",
  });

  const flattenFields = _.flattenDeep(
    _.map(prerequisiteFieldsData?.fields, _.values)
  );
  const fieldsToRemove = [];
  const fieldsWithValue = [];

  _.forEach(flattenFields, ({ field: key, type, step }) => {
    const [entity, field] = getEntityAndFieldType({
      key,
      type,
      unitPlanDetails,
      assessmentDetails,
    });

    const fieldHasValue = getFieldHasValue({
      field,
      step,
      context,
      fieldList,
      stepList,
      unitPlanFieldList: _.get(unitPlanTemplate, "field_list", {}),
      unitPlanStepList: _.get(unitPlanTemplate, "step_list", {}),
      assessmentDetails,
      isUnitDependent: entity === "UNIT",
      unitPlanDetails,
    });

    const isExcluded = _.includes(excludedFields, type);

    // if field was skipped but it contains value then remove it from excludedFields
    if (fieldHasValue && isExcluded) fieldsToRemove.push(type);

    if (isExcluded || fieldHasValue) fieldsWithValue.push(type);
  });

  const groupedFields = getGroupedFields({
    t,
    fields: _.flatten(_.values(prerequisiteFieldsData?.fields)),
    fieldList,
    unitPlanFieldList: _.get(unitPlanTemplate, "field_list", {}),
    unitPlanDetails,
    assessmentDetails,
    stepList,
    unitPlanStepList: _.get(unitPlanTemplate, "step_list", {}),
  });

  const isEveryPrerequisiteFieldFilled = _.isEqual(
    _.sortBy(_.map(flattenFields, "type")),
    _.sortBy(fieldsWithValue)
  );

  const measureAssessingRef = middlePaneRef?.Refs?.measureAssessing;

  return {
    unitPlanDetails,
    isEveryPrerequisiteFieldFilled,
    assessmentType,
    curriculumType: getCurrentCurriculumProgramType(state),
    fieldsToRemove,
    groupedFields,
    measureAssessingRef,
  };
};

const mapActionCreators = {
  setToastMsg,
  updateAIMessage,
  getAssessmentObj,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(PrerequisiteSelection);
